import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import PieChart from '../../../../charts/echarts/PieChart'
import { useChartContext } from '../../../../charts/echarts/ChartProvider'
import { usePipelineDataContextWithVariables } from '../../provider/DDPDataProvider'

function PipelinePieChart ({
  pipeline,
  variables,
  seriesAccessor,
  nameAccessor,
  valueAccessor,
  colorAccessor,
  valueFormat,
  categoryFormat,
  ...seriesProps
}) {
  const { setSeriesLoading } = useChartContext()
  const { data, isLoading } = usePipelineDataContextWithVariables(pipeline, variables)

  const chartData = useMemo(() => {
    const seriesData = get(data, seriesAccessor) || []
    const isMultiAccessor = Array.isArray(valueAccessor)

    const series = seriesData.map(item => {
      const datum = {
        name: get(item, nameAccessor),
        value: isMultiAccessor
          ? valueAccessor.map(accessor => get(item, accessor))
          : get(item, valueAccessor)
      }
      if (colorAccessor) {
        const color = get(item, colorAccessor)
        if (color != null) {
          datum.itemStyle = { color }
        }
      }
      return datum
    })

    return { series }
  }, [data, seriesAccessor, nameAccessor, valueAccessor, colorAccessor])

  useEffect(() => {
    setSeriesLoading(seriesProps.seriesId, isLoading)
  }, [seriesProps.seriesId, isLoading, setSeriesLoading])

  if (isLoading) return null

  return (
    <PieChart
      data={chartData.series}
      valueFormat={valueFormat}
      categoryFormat={categoryFormat}
      {...seriesProps}
    />
  )
}

PipelinePieChart.propTypes = {
  /** The data pipeline to execute */
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  variables: PropTypes.object,
  seriesAccessor: PropTypes.string,
  nameAccessor: PropTypes.string,
  valueAccessor: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]),
  colorAccessor: PropTypes.string,
  valueFormat: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]),
  categoryFormat: PropTypes.string
}

PipelinePieChart.defaultProps = {
  valueFormat: 'marketValue',
  categoryFormat: 'date',
  colorAccessor: undefined,
  height: 300
}

export default PipelinePieChart
